import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Panel, StatusBadge, Empty, Table } from "@/components/terminal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";
import { MessageSquare, Send, ShieldCheck } from "lucide-react";

const TOGGLES = [
  ["critical", "Critical Alerts"], ["trades", "Trades"], ["wildcard", "Wildcard"],
  ["tax", "Tax"], ["risk", "Risk"], ["income", "Income / Rewards"],
  ["daily_summary", "Daily Summary"], ["weekly_summary", "Weekly Summary"],
];

export default function NotificationsPanel() {
  const [st, setSt] = useState(null);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/notifications/status").then((r) => setSt(r.data)).catch(() => {});
    api.get("/notifications").then((r) => setEvents(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggle = async (key, value) => {
    try {
      const r = await api.put("/notifications/prefs", { [key]: value });
      setSt({ ...st, prefs: r.data });
      toast.success(`Notifications: ${key} ${value ? "enabled" : "disabled"}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const setQuiet = async (key, value) => {
    try {
      const r = await api.put("/notifications/prefs", { [key]: value });
      setSt({ ...st, prefs: r.data });
    } catch (e) { toast.error(apiError(e)); }
  };

  const send = async (path, label) => {
    setBusy(label);
    try {
      const r = await api.post(path);
      toast.success(`${label}: ${r.data.status}${r.data.provider ? ` via ${r.data.provider}` : ""}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (!st) return null;
  const prefs = st.prefs || {};
  const providers = st.providers || [];

  return (
    <Panel title="Notifications — SMS Alert System" testId="notifications-panel">
      <div className="panel border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-3 flex items-center gap-3 mb-4">
        <ShieldCheck className="w-5 h-5 gold-text shrink-0" />
        <p className="text-xs text-slate-300">{st.security} Destination: <span className="font-mono">{st.destination_masked || "not configured (set OWNER_PHONE_NUMBER server-side)"}</span></p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="space-y-3">
          <div className="metric-label">Providers</div>
          {providers.map((p) => (
            <div key={p.provider} className="flex items-center justify-between border-b border-[#222630] pb-2" data-testid={`notif-provider-${p.provider}`}>
              <div>
                <div className="text-sm text-slate-200">{p.label}</div>
                <div className="text-[10px] text-slate-500 max-w-[260px]">{p.message}</div>
              </div>
              <StatusBadge status={p.status} />
            </div>
          ))}
          <div className="text-[11px] text-slate-500">Active provider: <span className="font-mono text-slate-300">{st.active_provider}</span></div>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button data-testid="send-test-sms-button" size="sm" disabled={!!busy} onClick={() => send("/notifications/test", "Test SMS")}
              className="bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold"><Send className="w-3.5 h-3.5 mr-1.5" />SEND TEST SMS</Button>
            <Button data-testid="send-daily-summary-button" size="sm" variant="outline" disabled={!!busy} onClick={() => send("/notifications/summary/daily", "Daily summary")}
              className="border-[#3F4656] text-slate-300">DAILY SUMMARY</Button>
            <Button data-testid="send-weekly-summary-button" size="sm" variant="outline" disabled={!!busy} onClick={() => send("/notifications/summary/weekly", "Weekly summary")}
              className="border-[#3F4656] text-slate-300">WEEKLY SUMMARY</Button>
          </div>
        </div>

        <div className="space-y-2.5">
          <div className="metric-label">Alert Categories</div>
          {TOGGLES.map(([key, label]) => (
            <div key={key} className="flex items-center justify-between" data-testid={`notif-toggle-row-${key}`}>
              <span className="text-sm text-slate-300">{label}</span>
              <Switch data-testid={`notif-toggle-${key}`} checked={!!prefs[key]} onCheckedChange={(v) => toggle(key, v)} />
            </div>
          ))}
          <div className="pt-2 border-t border-[#222630] space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-300">Quiet hours</span>
              <Switch data-testid="notif-quiet-toggle" checked={!!prefs.quiet_hours_enabled} onCheckedChange={(v) => setQuiet("quiet_hours_enabled", v)} />
            </div>
            <div className="flex items-center gap-2">
              <Input type="time" data-testid="notif-quiet-start" value={prefs.quiet_start} onChange={(e) => setQuiet("quiet_start", e.target.value)}
                className="bg-[#121316] border-[#2D323E] font-mono h-8 text-xs w-28" />
              <span className="text-slate-500 text-xs">to</span>
              <Input type="time" data-testid="notif-quiet-end" value={prefs.quiet_end} onChange={(e) => setQuiet("quiet_end", e.target.value)}
                className="bg-[#121316] border-[#2D323E] font-mono h-8 text-xs w-28" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">CRITICAL bypasses quiet hours</span>
              <Switch data-testid="notif-critical-bypass-toggle" checked={!!prefs.critical_bypass_quiet} onCheckedChange={(v) => setQuiet("critical_bypass_quiet", v)} />
            </div>
          </div>
        </div>

        <div>
          <div className="metric-label mb-2">Recent Notification Events</div>
          {events.length === 0 ? <Empty>No notifications recorded yet.</Empty> : (
            <div className="max-h-72 overflow-y-auto space-y-2" data-testid="notification-events-list">
              {events.slice(0, 25).map((e) => (
                <div key={e.id} className="bg-[#121316] rounded-sm px-3 py-2 border border-[#222630]">
                  <div className="flex items-center justify-between text-[10px] text-slate-500">
                    <span className="font-mono">{new Date(e.timestamp).toLocaleString()}</span>
                    <span className={`font-semibold ${e.severity === "CRITICAL" ? "text-red-400" : e.severity === "WARNING" ? "text-amber-400" : "text-slate-400"}`}>{e.severity}</span>
                  </div>
                  <div className="text-xs text-slate-200 mt-0.5">{e.title}</div>
                  <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                    {e.provider || "—"} → {e.destination_masked || "no destination"} · {e.status}{e.attempt_count > 1 ? ` · ${e.attempt_count} attempts` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <p className="text-[10px] text-slate-600 mt-4 flex items-center gap-1.5"><MessageSquare className="w-3 h-3" /> Alerts are deduplicated with cooldown periods. Phone numbers are stored only as server-side secrets and always displayed masked.</p>
    </Panel>
  );
}
